/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/prop-types */
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import useFormController from '../hooks/useFormController';
import formControllerState from '../utils/formControllerState';
import { Checkmark } from '../private/icons/Checkmark';
import { ErrorFilled } from '../private/icons/ErrorFilled';
import { WarningFilled } from '../private/icons/WarningFilled';

const InputBase = (props) => {
  const {
    ariaDescribedby,
    autoComplete,
    autoFocus,
    classes,
    defaultValue,
    id,
    inputAttributes,
    component,
    name,
    onChange,
    onKeyDown,
    placeholder,
    readOnly,
    type = 'text',
    value,
    fullWidth,
    startAdornment,
    endAdornment,
    disableStatus,
    ...other
  } = props;

  const formControl = useFormController();

  const fcs = formControllerState({
    props,
    formControl,
    states: ['disabled', 'required', 'status', 'fullWidth']
  });

  const handleChange = (event, ...args) => {
    if (onChange) {
      onChange(event, ...args);
    }
  };

  const Input = component || 'input';

  const statusIcon = React.useMemo(() => {
    let icon;
    if (disableStatus) return icon;

    if (fcs.status === 'success') {
      icon = (
        <span className="sui-fill-success-strong" style={{ display: 'inherit' }}>
          <Checkmark size="small" color="inherit" />
        </span>
      );
    }
    if (fcs.status === 'error') {
      icon = (
        <span className="sui-fill-danger-strong" style={{ display: 'inherit' }}>
          <ErrorFilled size="small" color="inherit" />
        </span>
      );
    }
    if (fcs.status === 'warning') {
      icon = (
        <span className="sui-fill-warning-strong" style={{ display: 'inherit' }}>
          <WarningFilled size="small" color="inherit" />
        </span>
      );
    }
    return icon;
  }, [disableStatus, fcs.status]);

  const isStartAdornment = startAdornment || false;
  const isEndAdornment = endAdornment || statusIcon || false;

  const baseRootClasses = [
    'sui-font-regular sui-text-base sui-leading-tight',
    'sui-box-border sui-border-1 sui-border-solid',
    'sui-relative',
    'sui-cursor-text',
    'sui-inline-flex',
    'sui-items-center',
    'sui-rounded-base',
    'sui-h-11'
  ].join(' ');

  const rootClasses = classNames(baseRootClasses, {
    'sui-w-full': fcs.fullWidth,
    'hover:sui-border-hover focus:sui-border-hover sui-border-strong sui-bg-primary sui-text-primary': !fcs.status && !fcs.disabled,
    'sui-border-success-strong': !fcs.disabled && fcs.status === 'success',
    'sui-border-danger-strong': !fcs.disabled && fcs.status === 'error',
    'sui-border-warning-strong': !fcs.disabled && fcs.status === 'warning',
    'sui-border-inactive sui-bg-inactive sui-text-inactive sui-cursor-default sui-pointer-events-none': fcs.disabled,
    'sui-pl-3': isStartAdornment,
    'sui-pr-3': isEndAdornment
  }, classes?.root);

  const baseInputClasses = [
    'sui-text-current',
    'sui-border-0 sui-ring-0 sui-outline-none',
    'sui-box-content',
    'sui-m-0',
    'sui-block',
    'sui-min-w-0',
    'sui-w-full',
    'sui-h-11',
  ].join(' ');

  const inputClasses = classNames(baseInputClasses, {
    'sui-pl-3': !isStartAdornment,
    'sui-pr-3': !isEndAdornment,
    'sui-pl-0 sui-pr-3': isStartAdornment && !isEndAdornment,
    'sui-pl-3 sui-pr-0': isEndAdornment && !isStartAdornment
  }, classes?.input);

  return (
    <div className={rootClasses}>
      {startAdornment}
      <Input
        aria-invalid={fcs.status === 'error'}
        aria-describedby={ariaDescribedby}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={inputClasses}
        defaultValue={defaultValue}
        disabled={fcs.disabled}
        id={id}
        name={name}
        onChange={handleChange}
        // onBlur={handleBlur}
        // onFocus={handleFocus}
        onKeyDown={onKeyDown}
        // onKeyUp={onKeyUp}
        placeholder={placeholder}
        readOnly={readOnly}
        required={fcs.required}
        value={value}
        type={type}
        style={{ font: 'inherit', letterSpacing: 'inherit', background: 'none' }}
        {...inputAttributes}
        {...other}
      />
      {statusIcon || endAdornment}
    </div>
  );
};

InputBase.displayName = 'InputBase';

InputBase.propTypes = {
  /**
   * Identifies the element (or elements) that describes the element on which the attribute is set.
   */
  ariaDescribedby: PropTypes.string,
  /**
   * If "on", when a user starts to type in a field, the browser should display options to fill
   * in the field, based on earlier typed values.
   */
  autoComplete: PropTypes.string,
  /**
   * @ignore
   */
  autoFocus: PropTypes.bool,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * The default value for the input element.
   */
  defaultValue: PropTypes.any,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the status icons are disabled.
   */
  disableStatus: PropTypes.bool,
  /**
   * The id attribute that is added to the input element
   */
  id: PropTypes.string,
  /**
   * Attributes that are added to the input element.
   */
  inputAttributes: PropTypes.object,
  /**
   * The component used for the input node. Either a string to use a HTML element or a component.
   */
  component: PropTypes.elementType,
  /**
   * The name attribute that is added to the input element.
   */
  name: PropTypes.string,
  /**
   * The onChange attribute that is added to the input element.
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * The placeholder attribute that is added to the input element.
   */
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  /**
   * Start `InputAdornment` for this component.
   */
  startAdornment: PropTypes.node,
  /**
   * End `InputAdornment` for this component.
   */
  endAdornment: PropTypes.node,
  /**
   * The states of validation for the Input component.
   */
  status: PropTypes.oneOf(['error', 'success', 'warning']),
  /**
   * The type attribute that is added to the input element.
   */
  type: PropTypes.string,
  /**
   * The value attribute that is added to the input element.
   */
  value: PropTypes.any
};

export { InputBase };
